/**
 * Tracks how long a user dwells on an expanded FAQ question.
 *
 * Measures two durations per expansion:
 *  - `dwellTimeMs`     — time the expanded content was visible in the viewport.
 *  - `expandDurationMs` — wall-clock time from expand to collapse.
 *
 * Both are capped at MAX_DWELL_DURATION_MS. A "dwelled" analytics event is
 * fired automatically when the expansion ends, tagged with an end reason:
 *  - `manualCollapse` — user clicked to close.
 *  - `switchQuestion` — another question was expanded.
 *  - `navigated`      — the component unmounted (e.g. page navigation).
 */

import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

type DwellEndReason = 'manualCollapse' | 'switchQuestion' | 'navigated';

// Filter out stale expansions (e.g. user expanded a question and walked away).
const MAX_DWELL_DURATION_MS = 5 * 60 * 1000;

export type DwellMetrics = {
	dwellTimeMs: number;
	expandDurationMs: number;
};

type useFAQDwellEventProps = {
	spaceKey: string;
	questionPosition: number;
	isExpanded: boolean;
	expandedContentRef: React.RefObject<HTMLDivElement | null>;
};

type ActiveSession = {
	manualCollapse: boolean;
	computeMetrics: () => DwellMetrics;
};

export const useFAQDwellEvent = ({
	isExpanded,
	expandedContentRef,
	spaceKey,
	questionPosition,
}: useFAQDwellEventProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sessionRef = useRef<ActiveSession | null>(null);

	const isMountedRef = useRef(true);
	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[],
	);

	useEffect(() => {
		if (!isExpanded) {
			return;
		}

		const expandStart = Date.now();
		let accumulatedDwellMs = 0;
		let viewportEnteredAt: number | null = null;

		const computeMetrics = (): DwellMetrics => {
			let dwellTimeMs = accumulatedDwellMs;
			if (viewportEnteredAt != null) {
				dwellTimeMs += Date.now() - viewportEnteredAt;
			}
			return {
				dwellTimeMs: Math.min(dwellTimeMs, MAX_DWELL_DURATION_MS),
				expandDurationMs: Math.min(Date.now() - expandStart, MAX_DWELL_DURATION_MS),
			};
		};

		sessionRef.current = { manualCollapse: false, computeMetrics };

		const node = expandedContentRef.current;
		let observer: IntersectionObserver | undefined;

		if (node) {
			observer = new IntersectionObserver(([entry]) => {
				if (entry.isIntersecting) {
					viewportEnteredAt = Date.now();
				} else {
					if (viewportEnteredAt != null) {
						accumulatedDwellMs += Date.now() - viewportEnteredAt;
					}
					viewportEnteredAt = null;
				}
			});
			observer.observe(node);
		}

		return () => {
			observer?.disconnect();

			const session = sessionRef.current;
			sessionRef.current = null;

			let endReason: DwellEndReason;
			if (!isMountedRef.current) {
				endReason = 'navigated';
			} else if (session?.manualCollapse) {
				endReason = 'manualCollapse';
			} else {
				endReason = 'switchQuestion';
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'dwelled',
					actionSubject: 'section',
					actionSubjectId: 'expandedQuestion',
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'faq',
						spaceKey,
						questionPosition,
						dwellEndReason: endReason,
						...computeMetrics(),
					},
				},
			}).fire();
		};
	}, [isExpanded, expandedContentRef, spaceKey, questionPosition, createAnalyticsEvent]);

	return {
		markManualCollapse: (): DwellMetrics | null => {
			if (!sessionRef.current) {
				return null;
			}
			sessionRef.current.manualCollapse = true;
			return sessionRef.current.computeMetrics();
		},
	};
};
